import { useState } from "react";
import {
  ApplicationFeature,
  EntityType,
  ImportPaygapPersonnelDto,
  UploadedFileFeature,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { parseFileToRows } from "./useFileParser";
import { parseRowsToEmployees } from "./useEmployeeDataMapper";
import { importDataInBatches } from "./usePaygapImport";

import { useImportPersonnel, useUploadFile } from "@api";
import { useUserStore } from "@store/user";



export interface UploadStatus {
  file: File | null;
  status: "idle" | "uploading" | "success" | "error";
  message?: string;
  validationResults?: {
    requiredHeaders: string[];
    missingHeaders: string[];
    rowCount: number;
  };
}

export interface EmployeeData {
  key: string;
  age: string | number;
  gender: string | number;
  yearsOfService: string | number;
  educationAndTraining: string;
  employeeScarcity: string | number;
  employeeEvaluation: string | number;
  category: string;
  function: string;
  levelOfRequirements: string | number;
  levelOfRequirements2: string | number;
  levelOfRequirements3: string | number;
  professionalPosition: string | number;
  activityRatePercent: string | number;
  paidHours: string | number;
  basicWage: string | number;
  wageCompensationForVacation: string | number;
  wageCompensationDoctorFamilyLeave: string | number;
  sickLeaveIncomeCompensation: string | number;
  allowances: string | number;
  overtimeWage: string | number;
  overtimeAllowance: string | number;
  riskAllowance: string | number;
  standbyDuty: string | number;
  thirteenthMonthlyWage: string | number;
  monthlyBonus: string | number;
  standardBonus: string | number;
  specialBonus: string | number;
  fiftyFivePlusBonus: string | number;
  retirementBenefit: string | number;
  severancePay: string | number;
  specialPayments: string | number;
  supplementaryPensionInsurance: string | number;
  lifeInsurance: string | number;
  mealVouchers: string | number;
  cafeteria: string | number;
  carAllowance: string | number;
  employeeContribution: string | number;
  usualWeeklyWorkingHours: string | number;
  usualAnnualWorkingHours: string | number;
  statisticalPopulation: string | number;
  remarks: string;
  additionalInformation01: string;
  additionalInformation02: string;
  additionalInformation03: string;
  additionalInformation04: string;
  additionalInformation05: string;
}

export const usePaygapData = (): {
  // State
  uploadStatus: UploadStatus;
  employeeData: EmployeeData[];
  hasManualData: boolean;
  csvRawData: string[][] | null;
  importSuccess: boolean;
  dataFetchedFromDatabase: boolean;
  isPendingUploadFile: boolean;
  isPendingImportPersonnel: boolean;
  isSuccessImportPersonnel: boolean;
  importPersonnelError: Error | null;

  // Actions
  processFileUpload: (file: File) => Promise<void>;
  handleImportData: (dataToImport?: EmployeeData[]) => Promise<void>;
  setEmployeeData: (data: EmployeeData[]) => void;
  setHasManualData: (hasData: boolean) => void;
  setDataFetchedFromDatabase: (fetched: boolean) => void;
  setUploadStatus: (status: UploadStatus) => void;
  setImportSuccess: (success: boolean) => void;
} => {
  const { user } = useUserStore();
  const { uploadFileAsync, isPendingUploadFile } = useUploadFile(user);
  const {
    importPersonnel,
    isPendingImportPersonnel,
    isSuccessImportPersonnel,
    importPersonnelError,
  } = useImportPersonnel(user);

  const { t } = useTranslation();

  // State
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    file: null,
    status: "idle",
  });
  const [employeeData, setEmployeeData] = useState<EmployeeData[]>([]);
  const [hasManualData, setHasManualData] = useState(false);
  const [csvRawData, setCsvRawData] = useState<string[][] | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [dataFetchedFromDatabase, setDataFetchedFromDatabase] = useState(false);

  const processFileUpload = async (file: File): Promise<void> => {
    setUploadStatus({ file, status: "uploading" });
    setImportSuccess(false);

    try {
      const rows = await parseFileToRows(file);
      const parsedData = parseRowsToEmployees(rows);

      // Upload file to storage
      await uploadFileAsync({
        file,
        entityId: user?.activeCompanyId || "",
        databaseEntityType: EntityType.Company,
        uploadedFileFeature: UploadedFileFeature.FormData,
        relatedFeature: ApplicationFeature.PAYGAP,
      });

      const validationResults = {
        requiredHeaders: [],
        missingHeaders: [],
        rowCount: parsedData.length,
      };

      setUploadStatus({
        file,
        status: "success",
        message: t("paygap.upload.success"),
        validationResults,
      });

      setEmployeeData(parsedData);
      setHasManualData(false);
      setCsvRawData(rows);

      // Auto-import data (gender format is auto-detected during parsing)
      await handleImportData(parsedData);
    } catch (error: unknown) {
      setUploadStatus({
        file,
        status: "error",
        message: `Failed to upload or parse CSV/Excel file: ${error instanceof Error ? error.message : "Unknown error"}. Please check the file format.`,
      });
    }
  };

  const handleImportData = async (dataToImport?: EmployeeData[]): Promise<void> => {
    try {
      setImportSuccess(false);
      const dataToProcess = dataToImport || employeeData;

      console.log("dataToProcess", dataToProcess);

      await importDataInBatches(
        dataToProcess,
        async (request: ImportPaygapPersonnelDto) => {
          await importPersonnel(request);
        },
      );

      setImportSuccess(true);
    } catch {
      setImportSuccess(false);
    }
  };

  return {
    // State
    uploadStatus,
    employeeData,
    hasManualData,
    csvRawData,
    importSuccess,
    dataFetchedFromDatabase,
    isPendingUploadFile,
    isPendingImportPersonnel,
    isSuccessImportPersonnel,
    importPersonnelError,

    // Actions
    processFileUpload,
    handleImportData,
    setEmployeeData,
    setHasManualData,
    setDataFetchedFromDatabase,
    setUploadStatus,
    setImportSuccess,
  };
};
